import { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { useGetDrivers } from "../../../../api/fleet-management/drivers/drivers.hooks";
import {
  useCreateDriverExpenseMutation,
  useGetDriverExpenseById,
  useUpdateDriverExpenseMutation,
} from "../../../../api/fleet-management/driver-expenses/driver-expenses.hooks";
import DriverExpenseForm from "./_components/DriverExpenseForm";
import {
  buildDriverExpensePayload,
  initialDriverExpenseForm,
  mapDriverExpenseToForm,
  validateDriverExpenseForm,
} from "./utils/driver-expense.utils";

const DriverExpenseFormPage = () => {
  const history = useHistory();
  const { driverExpenseId } = useParams();
  const isEditMode = Boolean(driverExpenseId);

  const [form, setForm] = useState(initialDriverExpenseForm);
  const [initialFormState, setInitialFormState] = useState(initialDriverExpenseForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const { data: driverData, isLoading: isDriversLoading } = useGetDrivers();

  const {
    data: expenseData,
    isLoading: isExpenseLoading,
    isError: isExpenseError,
    error: expenseError,
  } = useGetDriverExpenseById(driverExpenseId);

  const {
    mutate: createDriverExpense,
    isLoading,
    isPending,
    error,
    reset,
  } = useCreateDriverExpenseMutation();

  const {
    mutate: updateDriverExpense,
    isLoading: isUpdating,
    isPending: isUpdatePending,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateDriverExpenseMutation();

  const drivers = Array.isArray(driverData) ? driverData : driverData?.drivers || driverData?.items || [];
  const associatedDriverName =
    (typeof expenseData?.driverId === "object" && expenseData?.driverId?.name) ||
    drivers.find((driver) => driver?._id === form.driverId)?.name ||
    "";

  useEffect(() => {
    if (!isEditMode || !expenseData) return;
    const nextForm = mapDriverExpenseToForm(expenseData);
    setForm(nextForm);
    setInitialFormState(nextForm);
  }, [expenseData, isEditMode]);

  const submitting = isLoading || isPending || isUpdating || isUpdatePending;
  const submitError = isEditMode ? updateError : error;

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "noOfDays" || key === "salaryPerDay") {
        const days = Number(key === "noOfDays" ? value : prev.noOfDays);
        const salary = Number(key === "salaryPerDay" ? value : prev.salaryPerDay);
        if (Number.isFinite(days) && Number.isFinite(salary) && days > 0 && salary > 0) {
          next.totalAmount = String(days * salary);
        }
      }

      return next;
    });
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const nextErrors = validateDriverExpenseForm(form);
    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage("");
    reset();
    resetUpdate();

    if (!validate()) return;

    const payload = buildDriverExpensePayload(form);

    if (isEditMode) {
      updateDriverExpense(
        { driverExpenseId, driverExpenseData: payload },
        {
          onSuccess: () => {
            history.push("/fleet-management/driver-expenses");
          },
        }
      );
      return;
    }

    createDriverExpense(payload, {
      onSuccess: () => {
        history.push("/fleet-management/driver-expenses");
      },
    });
  };

  if (isEditMode && isExpenseLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && isExpenseError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {expenseError?.response?.data?.message || "Failed to load driver expense details."}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => history.push("/fleet-management/driver-expenses")}
              aria-label="Back to driver expenses list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">
              {isEditMode ? "Edit Driver Expense" : "Create Driver Expense"}
            </h4>
          </div>
          <span className="badge bg-info text-dark">Fleet Management</span>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            {isEditMode ? "Update driver expense details." : "Fill in driver expense details."}
          </p>

          {successMessage && (
            <Alert variant="success" className="mb-4">
              {successMessage}
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError?.response?.data?.message ||
                `Failed to ${isEditMode ? "update" : "create"} driver expense. Please try again.`}
            </Alert>
          )}

          <DriverExpenseForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            isEditMode={isEditMode}
            associatedDriverName={associatedDriverName}
            drivers={drivers}
            isDriversLoading={isDriversLoading}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={() => {
              history.push("/fleet-management/driver-expenses");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DriverExpenseFormPage;
